import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, FileCheck, ArrowRightToLine, ArrowDown } from "lucide-react";
import { MdOutlineGroupAdd } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from "utils/constant";
import profileIcon from "../../../assets/contractor_icon.png";
import { useAlert } from "components/UI_components/Alert";

const AllWorkers = ({
  onOpenPayment,
  onOpenAttendance,
  onOpenNewWorker,
  userPermissions,
}) => {
  const navigate = useNavigate();
  const { siteId } = useParams();
  const { addAlert } = useAlert();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workerRates, setWorkerRates] = useState({}); // worker_id -> amount
  const [workerAttendanceCounts, setWorkerAttendanceCounts] = useState({}); // worker_id -> days present in month
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortBy, setSortBy] = useState("name_asc");
  const sortRef = useRef(null);

  // Compute current month string (e.g., 2025-10) and days in current month
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth(); // 0-based
  const currentMonthString = `${currentYear}-${String(
    currentMonthIndex + 1
  ).padStart(2, "0")}`;
  const daysInCurrentMonth = new Date().getDate();

  // Sort dropdown close on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (isSortOpen && sortRef.current && !sortRef.current.contains(e.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isSortOpen]);

  // Sorted workers based on selection
  const sortedWorkers = useMemo(() => {
    const arr = [...workers];
    const getDaysPresent = (w) => workerAttendanceCounts[w.worker_id] || 0;
    const getAttendancePercent = (w) =>
      daysInCurrentMonth > 0 ? (getDaysPresent(w) / daysInCurrentMonth) * 100 : 0;
    const getTotalPay = (w) => {
      const rate = workerRates[w.worker_id];
      const present = getDaysPresent(w);
      if (rate == null) return null;
      return rate * present;
    };
    const compareNumberWithNullLast = (a, b, dir = "desc") => {
      const aNull = a == null;
      const bNull = b == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      return dir === "desc" ? b - a : a - b;
    };

    switch (sortBy) {
      case "name_asc":
        arr.sort((a, b) => (a.worker_name || "").localeCompare(b.worker_name || ""));
        break;
      case "name_desc":
        arr.sort((a, b) => (b.worker_name || "").localeCompare(a.worker_name || ""));
        break;
      case "attendance_desc":
        arr.sort((a, b) => getAttendancePercent(b) - getAttendancePercent(a));
        break;
      case "attendance_asc":
        arr.sort((a, b) => getAttendancePercent(a) - getAttendancePercent(b));
        break;
      case "pay_desc":
        arr.sort((a, b) => compareNumberWithNullLast(getTotalPay(a), getTotalPay(b), "desc"));
        break;
      case "pay_asc":
        arr.sort((a, b) => compareNumberWithNullLast(getTotalPay(a), getTotalPay(b), "asc"));
        break;
      case "days_desc":
        arr.sort((a, b) => getDaysPresent(b) - getDaysPresent(a));
        break;
      case "days_asc":
        arr.sort((a, b) => getDaysPresent(a) - getDaysPresent(b));
        break;
      default:
        break;
    }
    return arr;
  }, [workers, workerRates, workerAttendanceCounts, sortBy, daysInCurrentMonth]);

  useEffect(() => {
    const fetchWorkers = async () => {
      const site = localStorage.getItem("site");
      const accessToken = localStorage.getItem("access");
      try {
        const response = await fetch(`${BASE_URL}/worker/workers-page/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            worker_type: "Worker",
            sites: parseInt(site),
          }),
        });
        const data = await response.json();
        if (data.status) {
          setWorkers(data.data || []);
        } else {
          setWorkers([]);
        }
      } catch (e) {
        setWorkers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkers();
  }, []);

  useEffect(() => {
    const fetchWorkerRates = async () => {
      if (!workers || workers.length === 0) return;

      const accessToken = localStorage.getItem("access");
      try {
        const results = await Promise.all(
          workers.map(async (w) => {
            try {
              const res = await fetch(
                `${BASE_URL}/worker/get-workerpayment-of-worker/`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                  },
                  body: JSON.stringify({ worker_id: w.worker_id }),
                }
              );
              const data = await res.json();
              if (
                data?.status &&
                Array.isArray(data.data) &&
                data.data.length > 0
              ) {
                const record = data.data[0];
                return [w.worker_id, record.amount];
              }
            } catch (_) {}
            return [w.worker_id, null];
          })
        );

        const map = {};
        results.forEach(([id, amt]) => {
          map[id] = amt;
        });
        setWorkerRates(map);
      } catch (_) {
        // ignore, keep defaults
      }
    };

    fetchWorkerRates();
  }, [workers]);

  useEffect(() => {
    const fetchWorkerAttendance = async () => {
      if (!workers || workers.length === 0) return;

      const accessToken = localStorage.getItem("access");
      try {
        const results = await Promise.all(
          workers.map(async (w) => {
            try {
              const res = await fetch(
                `${BASE_URL}/worker/single-worker-attendance-history/`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                  },
                  body: JSON.stringify({
                    worker_id: w.worker_id,
                    month: currentMonthString,
                  }),
                }
              );
              const data = await res.json();
              if (data?.status && Array.isArray(data.data)) {
                // Count only 'Present' entries if attendance field exists; otherwise, count all
                const presentCount = data.data.filter((r) =>
                  r && typeof r.attendance === "string"
                    ? r.attendance.toLowerCase() === "present"
                    : true
                ).length;
                return [w.worker_id, presentCount];
              }
            } catch (_) {}
            return [w.worker_id, 0];
          })
        );

        const map = {};
        results.forEach(([id, cnt]) => {
          map[id] = cnt;
        });
        setWorkerAttendanceCounts(map);
      } catch (_) {
        // ignore, keep defaults
      }
    };

    fetchWorkerAttendance();
  }, [workers, currentMonthString]);

  return (
    <div>
      {/* Primary and secondary actions */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => {
            if (
              userPermissions?.edit_worker_management ||
              userPermissions?.is_admin ||
              userPermissions?.joined_as === "owner"
            ) {
              onOpenAttendance();
            } else {
              addAlert(
                "You must have edit worker management permission to mark attendance.",
                "error"
              );
            }
          }}
          className="px-3 py-2 bg-[#ECE6F0] text-[#3B0069] rounded-xl font-medium flex items-center gap-2"
        >
          <span className="flex items-center justify-center rounded-full p-1">
            <FileCheck className="w-4 h-4" />
          </span>
          Mark Attendance
        </button>

        <div className="flex items-center gap-3">
          <div className="relative" ref={sortRef}>
            <button
              onClick={() => setIsSortOpen((s) => !s)}
              className="text-text-clr px-4 bg-[#ECE6F0] rounded-3xl py-1 font-semibold flex items-center"
            >
              Sort <ArrowDown className="w-4 h-4 inline-block ml-1" />
            </button>
            {isSortOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border rounded-xl shadow-lg z-20">
                <div className="py-1 text-sm">
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      setSortBy("name_asc");
                      setIsSortOpen(false);
                    }}
                  >
                    Name (A → Z)
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      setSortBy("name_desc");
                      setIsSortOpen(false);
                    }}
                  >
                    Name (Z → A)
                  </button>
                  <div className="h-px bg-gray-200 my-1" />
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      setSortBy("days_desc");
                      setIsSortOpen(false);
                    }}
                  >
                    Days Present (High → Low)
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      setSortBy("days_asc");
                      setIsSortOpen(false);
                    }}
                  >
                    Days Present (Low → High)
                  </button>
                  <div className="h-px bg-gray-200 my-1" />
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      setSortBy("attendance_desc");
                      setIsSortOpen(false);
                    }}
                  >
                    Attendance % (High → Low)
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      setSortBy("attendance_asc");
                      setIsSortOpen(false);
                    }}
                  >
                    Attendance % (Low → High)
                  </button>
                  <div className="h-px bg-gray-200 my-1" />
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      setSortBy("pay_desc");
                      setIsSortOpen(false);
                    }}
                  >
                    Total Pay (High → Low)
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      setSortBy("pay_asc");
                      setIsSortOpen(false);
                    }}
                  >
                    Total Pay (Low → High)
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              if (
                userPermissions?.edit_worker_management ||
                userPermissions?.is_admin ||
                userPermissions?.joined_as === "owner"
              ) {
                onOpenPayment();
              } else {
                addAlert(
                  "You must have edit worker management permission to add worker payments.",
                  "error"
                );
              }
            }}
            className="px-3 py-2 bg-[#3B0069] text-white rounded-xl font-medium flex items-center gap-2"
          >
            <span className="flex items-center justify-center rounded-full p-1 bg-[#58267F]">
              <Plus className="w-4 h-4" />
            </span>
            Transaction
          </button>
          <button
            onClick={() => {
              if (
                userPermissions?.edit_worker_management ||
                userPermissions?.is_admin ||
                userPermissions?.joined_as === "owner"
              ) {
                onOpenNewWorker();
              } else {
                addAlert(
                  "You must have edit worker management permission to add new workers",
                  "error"
                );
              }
            }}
            className="px-3 py-2 bg-[#3B0069] text-white rounded-xl font-medium flex items-center gap-2"
          >
            <span className="flex items-center justify-center rounded-full p-1 bg-[#58267F]">
              <MdOutlineGroupAdd className="w-4 h-4" />
            </span>
            Worker
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-3">
        Total {workers.length} workers
      </p>

      {/* Header row mimic
      <div className="hidden md:grid grid-cols-12 px-4 py-2 text-xs text-[#9E9E9E]">
        <div className="col-span-4">Name</div>
        <div className="col-span-2">Days Present</div>
        <div className="col-span-2">Attendance</div>
        <div className="col-span-2">Total Pay</div>
        <div className="col-span-2" />
      </div> */}

      {/* List */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-xs text-[#8F8F8F] px-4 py-6">Loading...</div>
        ) : workers.length === 0 ? (
          <div className="text-xs text-[#8F8F8F] px-4 py-6">
            No workers found
          </div>
        ) : (
          sortedWorkers.map((w) => (
            <div
              key={w.worker_id}
              className="bg-white rounded-xl shadow-sm px-4 py-3 cursor-pointer"
              onClick={() => navigate(`/site/${siteId}/workers/${w.worker_id}`)}
              // on hover scale up
              style={{
                transition: "transform 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.01)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <div className="grid grid-cols-12 items-center">
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-9 h-9 bg-gray-200 rounded-full flex items-center justify-center">
                    <img src={profileIcon} alt="profile" />
                  </div>
                  <div>
                    <div className="text-[16px] font-medium text-[#232323]">
                      {w.worker_name}
                    </div>
                    <div className="text-sm text-[#232323]">
                      {workerRates[w.worker_id] != null
                        ? `₹ ${workerRates[w.worker_id]}/day`
                        : "—"}
                    </div>
                  </div>
                </div>
                <div className="col-span-2 text-sm">
                  <div className="text-[14px] text-gray-500">Days Present</div>
                  <div className="text-[16px] font-medium text-[#232323]">
                    {workerAttendanceCounts[w.worker_id] || 0}/
                    {daysInCurrentMonth}
                  </div>
                </div>
                <div className="col-span-2 text-sm">
                  <div className="text-[14px] text-gray-500">Attendance</div>
                  <div className="text-[16px] font-medium text-[#232323]">
                    {Math.round(
                      ((workerAttendanceCounts[w.worker_id] || 0) /
                        daysInCurrentMonth) *
                        100
                    )}
                    %
                  </div>
                </div>
                <div className="col-span-2 text-sm">
                  <div className="text-[14px] text-gray-500">Total Pay</div>
                  <div className="text-[16px] font-medium text-[#232323]">
                    {workerRates[w.worker_id] != null
                      ? `₹ ${(
                          workerRates[w.worker_id] *
                          (workerAttendanceCounts[w.worker_id] || 0)
                        ).toLocaleString("en-IN")}`
                      : "—"}
                  </div>
                </div>
                <div className="col-span-2 flex justify-end">
                  <ArrowRightToLine className="w-5 h-5 text-[#232323]" />
                </div>
                <div className="col-span-2 flex justify-end" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AllWorkers;
